import { computed, Injectable, NgZone, Signal, signal, WritableSignal, isDevMode } from "@angular/core";
import { ITag } from "../_models/tag.model";
import { IObservation } from "../_models/observation.model";
import { Global } from "../_constants/global.variables";
import { Subject } from 'rxjs';
import { IAssetState } from "../_models/asset-state.model";

export interface IBucketItem {
  tag: ITag;
  startUtc?: number; // inclusive UTC epoch ms
  endUtc?: number;   // inclusive UTC epoch ms
}

export interface ITagBucketConfig {
  startUtc?: number;
  endUtc?: number;
}

@Injectable({ providedIn: "root" })
export class TagCacheService {
  public assetsObject: Record<number, any> = {};
  private devWriteHandlers = new Set<(tagId: number, value: number) => void>();


  private allTagsComputed?: Signal<ITag[]>;
  private allOrgAlarmTagsComputed?: Signal<ITag[]>;
  public tagBucket = signal<IBucketItem[]>([]);
  public tagBucketConfig = signal<ITagBucketConfig>({});
  private readonly bucketIdSet = computed(() => new Set(this.tagBucket().map(b => b.tag.Id)));

  private devStdValues = new Map<string, number>();

  private key(assetId: number, stdId: number) { return `${assetId}:${stdId}`; }

  // Category sets
  private readonly elevatorRotundaStandardIds = new Set<number>([13591, 13605, 13607, 13608, 13721, 13723, 13724, 13729, 19069, 55768, 56751, 56755, 56760, 56768, 56769, 56776, 56779, 56780, 56781, 56782, 56783, 56784, 56785, 56786, 56787, 56788, 56789, 56790, 56791, 56792, 56793, 56794, 56795, 56796, 56798]);

  private readonly movePreventionStandardIds = new Set<number>([3782, 13633, 13640, 56266, 56267, 1887, 14993, 54256, 54257, 13378, 4525, 4527, 14839, 14846, 1879, 13005, 20011, 12465, 56886, 12386, 13530, 13510, 13491, 15575, 12384, 13358, 13359, 13576, 12467, 13363, 54299, 15580, 13051, 13368, 12388, 56379, 13488, 12433, 13516, 13553, 56887, 12405, 12469, 13449, 13535, 56757, 3499, 13503, 12466, 13596, 13494, 3930, 14633, 12443, 15664, 12453, 12404, 12431, 12455, 12406, 15666, 12400, 13445, 13548]);

  private readonly perfectTurnStandardIds = {
    PBB: new Set<number>([12245, 70720, 54271, 54283, 54284, 59349, 59350, 59351, 59352, 59356]),
    PCA: new Set<number>([2736, 12374, 15215, 15220, 54283, 54284]),
    GPU: new Set<number>([1942, 12374, 15166, 15898, 54283, 54284]),
    JetDock: new Set<number>([59349, 59350, 59351, 59352, 59356]),
  };

  private serviceName: string = "tag-cache: ";
  public initializeIndexes: boolean = true;

  //-- tag / observation maps
  public tagMap = new Map<string, WritableSignal<ITag>>();
  public obsMap = new Map<string, IObservation>();

  //-- Indexes / Indices
  private siteIndex = new Map<number, WritableSignal<ITag>[]>();
  private siteAlarmIndex = new Map<number, WritableSignal<ITag>[]>();

  private systemIndex = new Map<number, WritableSignal<ITag>[]>();
  private systemAlarmIndex = new Map<number, WritableSignal<ITag>[]>();

  private assetIndex = new Map<number, WritableSignal<ITag>[]>();
  private assetAlarmIndex = new Map<number, WritableSignal<ITag>[]>();

  private standardNameIndex = new Map<number, WritableSignal<ITag>[]>();
  private tagNameIndex = new Map<number, WritableSignal<ITag>[]>();
  private tagIdAndSeverityIndex = new Map<number, WritableSignal<ITag>[]>();

  private movePreventionIndex = new Map<number, WritableSignal<ITag>[]>();
  private elevatorRotundaIndex = new Map<number, WritableSignal<ITag>[]>();
  private perfectTurnIndex = new Map<string, WritableSignal<ITag>[]>();
  private optionTagIndex = new Map<number, WritableSignal<ITag>[]>();

  // New maps (parallel to any existing name-based ones)
  private tagIdIndex = new Map<number, Signal<ITag>>();      // id -> signal(ITag)
  private tagIdComputedMap = new Map<string, Signal<ITag[]>>(); // "id1,id2" -> signal([ITag,...])

  //-- filtered, computed maps
  private siteComputedMap = new Map<number, Signal<ITag[]>>();
  private siteAlarmComputedMap = new Map<number, Signal<ITag[]>>();

  private systemComputedMap = new Map<number, Signal<ITag[]>>();
  private systemAlarmComputedMap = new Map<number, Signal<ITag[]>>();

  private assetComputedMap = new Map<number, Signal<ITag[]>>();
  private assetAlarmComputedMap = new Map<number, Signal<ITag[]>>();
  private tagNameComputedMap = new Map<number, Signal<ITag[]>>();
  private standardNameComputedMap = new Map<number, Signal<ITag[]>>();
  private tagIdAndSeverityComputedMap = new Map<number, Signal<ITag[]>>();

  private movePreventionComputedMap = new Map<number, Signal<ITag[]>>();
  private elevatorRotundaComputedMap = new Map<number, Signal<ITag[]>>();
  private perfectTurnComputedMap = new Map<string, Signal<ITag[]>>();
  private optionTagComputedMap = new Map<number, Signal<ITag[]>>();
  public ingestTagCounter: number = 0;
  public flushCounter: number = 0;
  public tagsUpdatedCounter: number = 0;

  // TagCacheService additions
  private readonly FLUSH_MS = 1000;
  private pending = new Map<number, { obs: IObservation; tag: ITag }>();
  private pendingAssetIds = new Set<number>();
  private flushTimer: any = null;

  // Emits the merged patches to the component after every flush
  public readonly assetStatePatches$ = new Subject<Map<number, Partial<IAssetState>>>();

  // Optional: single 'tick' to gate expensive computeds (see step 2)
  private flushTick = signal(0);

  /** Reset all cached in-memory tags, observations, computed signals, maps, indices, and pending flush queues */
  public resetTagCache(): void {
    console.log(this.serviceName + "resetTagCache invoked.");
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
    this.pending.clear();
    this.pendingAssetIds.clear();
    this.tagMap.clear();
    this.obsMap.clear();
    this.siteIndex.clear();
    this.siteAlarmIndex.clear();
    this.systemIndex.clear();
    this.systemAlarmIndex.clear();
    this.assetIndex.clear();
    this.assetAlarmIndex.clear();
    this.standardNameIndex.clear();
    this.tagNameIndex.clear();
    this.tagIdAndSeverityIndex.clear();
    this.movePreventionIndex.clear();
    this.elevatorRotundaIndex.clear();
    this.perfectTurnIndex.clear();
    this.optionTagIndex.clear();
    this.tagIdIndex.clear();
    this.tagIdComputedMap.clear();
    this.siteComputedMap.clear();
    this.siteAlarmComputedMap.clear();
    this.systemComputedMap.clear();
    this.systemAlarmComputedMap.clear();
    this.assetComputedMap.clear();
    this.assetAlarmComputedMap.clear();
    this.tagNameComputedMap.clear();
    this.standardNameComputedMap.clear();
    this.tagIdAndSeverityComputedMap.clear();
    this.movePreventionComputedMap.clear();
    this.elevatorRotundaComputedMap.clear();
    this.perfectTurnComputedMap.clear();
    this.optionTagComputedMap.clear();
    this.dockedBySystemComputedMap.clear();
    this.valueSigMap.clear();
    this.allTagsComputed = undefined;
    this.allOrgAlarmTagsComputed = undefined;
    this.tagBucket.set([]);
    this.tagBucketConfig.set({});
    this.devStdValues.clear();
    this.devWriteHandlers.clear();
    this.ingestTagCounter = 0;
    this.flushCounter = 0;
    this.tagsUpdatedCounter = 0;
    this.notifyTagMapReplaced("resetTagCache");
  }

  /**
   * Forces computeds that depend on tag-map structure (Map entries being added/replaced)
   * to re-evaluate. Use this after bulk/initial cache loads that replace `tagMap`/`obsMap`
   * without going through `ingestTag`/`ingestBulk`.
   */
  public notifyTagMapReplaced(reason?: string): void {
    // Ensure UI change detection sees this even if called from runOutsideAngular().
    this.zone.run(() => this.flushTick.update(v => v + 1));
    if (isDevMode() && reason) {
      console.log(this.serviceName + "notifyTagMapReplaced: " + reason);
    }
  }

  private toBool(v: any): boolean | undefined {
    if (v === undefined || v === null) return undefined;
    if (typeof v === "string") {
      const s = v.trim().toLowerCase();
      if (s === "true" || s === "1") return true;
      if (s === "false" || s === "0") return false;
    }
    if (v === true || v === 1) return true;
    if (v === false || v === 0) return false;
    return !!v;
  }

  private dockedBySystemComputedMap = new Map<number, Signal<boolean | undefined>>();

  // Map StandardId -> function that returns a state patch
  private readonly STATE_FIELD_BY_STANDARD: Record<number, (v: any) => Partial<IAssetState>> = {
    12245: v => ({ Docked: this.toBool(v) }),
    70720: v => ({ Docked: this.toBool(v) }),
    54283: v => ({ MaintenanceMode: this.toBool(v) }),
    54284: v => ({ OutOfService: this.toBool(v) }),
    54271: v => ({ InMotion: this.toBool(v) }),
    12374: v => ({ On: this.toBool(v) }),
    1835: v => ({ AmbientTemp: v }),
    2736: v => ({ DischargeTemp: v }),
    15220: v => ({ BridgeMode: this.toBool(v) }),
    54260: v => ({ Latitude: v }),
    54261: v => ({ Longitude: v }),
    79720: v => ({ NotMeetingPerformance: this.toBool(v) }),
    79798: v => ({ NotMeetingBridgeCoolPerformance: this.toBool(v) }),
    79830: v => ({ NotMeetingHeatPerformance: this.toBool(v) }),
    15219: v => ({ ModeOfOperation: v }),
    2244: v => ({ VoltsInAvg: v }),
    1935: v => ({ VoltsOutAvg: v }),
    15032: v => ({ AmpsInAvg: v }),
    1942: v => ({ AmpsOutAvg: v }),
  };

  // --- Live value bus (no extra service needed)
private valueSigMap = new Map<number, WritableSignal<number | null>>();

public valueSignalForTagId(tagId: number): Signal<number | null> {
  let s = this.valueSigMap.get(tagId);
  if (!s) { s = signal<number | null>(null); this.valueSigMap.set(tagId, s); }
  return s.asReadonly();
}

public pushValue(tagId: number, value: number): void {
  let s = this.valueSigMap.get(tagId);
  if (!s) { s = signal<number | null>(null); this.valueSigMap.set(tagId, s); }
  s.set(value);
}

public signalForTagId(tagId: number): Signal<ITag> | undefined {
  return this.tagMap.get(tagId.toString());
}

/** Compose live coords for an asset from its LAT/LNG tagIds. */
public coordSignalForAsset(
  assetId: number,
  latStdId: number,
  lngStdId: number
): Signal<{ lat: number; lng: number } | null> {
  const tagsSig = this.getTagsForAsset(assetId);
  return computed(() => {
    const tags = tagsSig() ?? [];
    const latTag = tags.find(t => t.JBTStandardObservationId === latStdId);
    const lngTag = tags.find(t => t.JBTStandardObservationId === lngStdId);
    if (!latTag || !lngTag) return null;

    const latLive = this.valueSignalForTagId(latTag.Id)();
    const lngLive = this.valueSignalForTagId(lngTag.Id)();

    // fallback to LastObs if no live yet
    const lat = (latLive ?? +latTag.LastObs?.Value);
    const lng = (lngLive ?? +lngTag.LastObs?.Value);

    if (!isFinite(lat) || !isFinite(lng)) return null;
    return { lat, lng };
  });
}


  constructor(private zone: NgZone) { }

  /**
   * Reactive "is docked" signal for a gate system, derived from tag cache.
   * Uses JBT Standard Observation Id 12245 (PBB Docked) or 70720.
   * If multiple docked tags exist under the system, returns true if any are true,
   * otherwise false if any are false, otherwise undefined.
   */
  public dockedSignalForSystem(systemId: number): Signal<boolean | undefined> {
    if (!this.dockedBySystemComputedMap.has(systemId)) {
      const tagsSig = this.getTagsForSystem(systemId);
      this.dockedBySystemComputedMap.set(
        systemId,
        computed(() => {
          const tags = tagsSig() ?? [];
          const vals = tags
            .filter(t => t?.JBTStandardObservationId === 12245 || t?.JBTStandardObservationId === 70720)
            .map(t => this.toBool(t?.LastObs?.Value))
            .filter(v => typeof v === "boolean") as boolean[];

          if (!vals.length) return undefined;
          if (vals.some(v => v === true)) return true;
          return false;
        })
      );
    }
    return this.dockedBySystemComputedMap.get(systemId)!;
  }

  /** Called by TagWatchService (or others) to receive dev writes */
registerDevWriteHandler(fn: (tagId: number, value: number) => void) {
  this.devWriteHandlers.add(fn);
  return () => this.devWriteHandlers.delete(fn); // return un-register function
}

  // Call this when you ingest/populate tags so both indexes stay in sync:
  indexTagSignal(tagSig: Signal<ITag>) {
    const t = tagSig();
    if (!t || typeof t.Id !== 'number') return;
    this.tagIdIndex.set(t.Id, tagSig);        // 👈 ID index
    // (Keep your name index logic as-is if you use it elsewhere)
  }

  ingestTag(updated: IObservation, updatedTag: ITag): void {
    // console.log(this.serviceName + "ingestTag invoked. updated: %O", updated);
    // console.log(this.serviceName + "ingestTag invoked. updatedTag: %O", updatedTag);

    const existingObservation = this.obsMap.get(updated.TagId.toString());
    const changed =
      !existingObservation ||
      this.initializeIndexes === true || //-- this is for the initial processing of Angular Signal data.
      existingObservation?.Value !== updated?.Value ||
      existingObservation.DateInMilliseconds !== updated.DateInMilliseconds;

    //console.log(this.serviceName + "changed: " + changed + ", existingObservation: %O", existingObservation);

    if (changed) {

      this.obsMap.set(updated.TagId.toString(), updated); //--updating Main Tag Obs mapped array here.
      const key = updatedTag.Id.toString();
      let tagSig = this.tagMap.get(key);
      const isNew = !tagSig;

      if (isNew) {
        tagSig = signal(updatedTag);
        this.tagMap.set(key, tagSig);
        this.indexTagSignal(tagSig);               // ✅ NEW: register by id
      } else {
        tagSig!.set(updatedTag);               // update the signal
        this.indexTagSignal(tagSig!);          // ✅ NEW: ensure id → signal is set
      }

      // Keep alarm indexes in sync for every update (not just initial index build).
      this.setUpAlarmTags(updatedTag);

      // console.log(this.serviceName + "initializeIndexes: ", this.initializeIndexes);
      // Index for fast lookup later
      if (this.initializeIndexes) {
        //console.log(this.serviceName + "initializing indexes...");
        if (updatedTag.SiteId != null) {
          if (!this.siteIndex.has(updatedTag.SiteId)) this.siteIndex.set(updatedTag.SiteId, []);
          this.siteIndex.get(updatedTag.SiteId)!.push(tagSig!);
          //console.log(this.serviceName + "this.siteIndex: %O", this.siteIndex);
        }

        if (updatedTag.Asset?.ParentSystemId != null) {
          if (!this.systemIndex.has(updatedTag.Asset?.ParentSystemId)) this.systemIndex.set(updatedTag.Asset?.ParentSystemId, []);
          this.systemIndex.get(updatedTag.Asset?.ParentSystemId)!.push(tagSig!);
          //console.log(this.serviceName + "this.systemIndex: %O", this.systemIndex);
        }

        if (updatedTag.AssetId != null) {
          if (!this.assetIndex.has(updatedTag.AssetId)) this.assetIndex.set(updatedTag.AssetId, []);
          this.assetIndex.get(updatedTag.AssetId)!.push(tagSig!);
          //console.log(this.serviceName + "this.assetIndex: %O", this.assetIndex);
        }

        if (updatedTag.JBTStandardObservationId != null) {
          if (!this.standardNameIndex.has(updatedTag.JBTStandardObservationId)) this.standardNameIndex.set(updatedTag.JBTStandardObservationId, []);
          this.standardNameIndex.get(updatedTag.JBTStandardObservationId)!.push(tagSig!);
          //console.log(this.serviceName + "this.standardNameIndex: %O", this.standardNameIndex);
        }

        if ((updatedTag.IsAlarm || updatedTag.IsCritical) && updatedTag.LastObs.Value === updatedTag.ValueWhenActive) {
          if (!this.tagIdAndSeverityIndex.has(updatedTag.Id)) this.tagIdAndSeverityIndex.set(updatedTag.Id, []);
          this.tagIdAndSeverityIndex.get(updatedTag.Id)!.push(tagSig!);
          //console.log(this.serviceName + "this.tagIdAndSeverityIndex: %O", this.tagIdAndSeverityIndex);
        }

        if (updatedTag.JBTStandardObservationId != undefined && this.movePreventionStandardIds.has(updatedTag.JBTStandardObservationId)) {
          if (!this.movePreventionIndex.has(updatedTag.Id)) this.movePreventionIndex.set(updatedTag.Id, []);
          this.movePreventionIndex.get(updatedTag.Id)!.push(tagSig!);
          //console.log(this.serviceName + "this.movePreventionIndex: %O", this.movePreventionIndex);
        }

        if (updatedTag.JBTStandardObservationId != undefined && this.elevatorRotundaStandardIds.has(updatedTag.JBTStandardObservationId)) {
          if (!this.elevatorRotundaIndex.has(updatedTag.Id)) this.elevatorRotundaIndex.set(updatedTag.Id, []);
          this.elevatorRotundaIndex.get(updatedTag.Id)!.push(tagSig!);
          //console.log(this.serviceName + "this.elevatorRotundaIndex: %O", this.elevatorRotundaIndex);
        }

        for (const category of ["PBB", "PCA", "GPU", "JetDock"] as const) {
          if (updatedTag.JBTStandardObservationId != undefined && this.perfectTurnStandardIds[category]?.has(updatedTag.JBTStandardObservationId)) {
            const assetTypeName = category === 'JetDock' ? 'JetDock' : updatedTag.Asset?.AssetType?.Name;
            const indexKey = assetTypeName ?? 'PBB';
            if (!this.perfectTurnIndex.has(indexKey)) this.perfectTurnIndex.set(indexKey, []);
            this.perfectTurnIndex.get(indexKey)!.push(tagSig!);
            //console.log(this.serviceName + "this.perfectTurnIndex: %O", this.perfectTurnIndex);
          }
        }

        if (updatedTag.JBTStandardObservationId != undefined && updatedTag.JBTStandardObservation?.Name.includes("option")) {
          if (!this.optionTagIndex.has(updatedTag.Id)) this.optionTagIndex.set(updatedTag.Id, []);
          this.optionTagIndex.get(updatedTag.Id)!.push(tagSig!);
          //console.log(this.serviceName + "this.optionTagIndex: %O", this.optionTagIndex);
        }
      }
      if(isNew) this.flushTick.update(v => v + 1); // Gate computeds that opt in (step 2)
    }
  }

  private flushNow() {
    this.flushCounter++;
    if (this.pending.size === 0) return;

    const list = Array.from(this.pending.values());
    this.pending.clear();

    this.ingestBulk(list);

    // hand back the affected assets, then reset
    this.updateAssetsAffected(list);
  }

  queueTagUpdates(updates: Array<{ obs: IObservation; tag: ITag }>) {
    for (const u of updates) {
      this.pending.set(u.tag.Id, u);                      // keep only latest per tag
      if (u.tag.AssetId != null) this.pendingAssetIds.add(u.tag.AssetId);
    }
    if (!this.flushTimer) {
      this.flushTimer = setTimeout(() => { this.flushTimer = null; this.flushNow(); }, this.FLUSH_MS);
    }
  }

  ingestBulk(list: any) {
    // Apply all tag/observation changes in one pass
    for (const { obs, tag } of list) {
      const existing = this.obsMap.get(obs.TagId.toString());
      const changed =
        !existing || this.initializeIndexes === true ||
        existing.Value !== obs.Value ||
        existing.DateInMilliseconds !== obs.DateInMilliseconds;

      this.ingestTagCounter++;

      if (!changed) continue;

      this.obsMap.set(obs.TagId.toString(), obs);
      this.tagsUpdatedCounter++;

      const key = tag.Id.toString();
      let sig = this.tagMap.get(key);
      if (!sig) {
        sig = signal(tag);
        this.tagMap.set(key, sig);
        this.indexTagSignal(sig);


        if (this.initializeIndexes) {
          if (tag.SiteId != null) (this.siteIndex.get(tag.SiteId) ?? this.siteIndex.set(tag.SiteId, []).get(tag.SiteId)!).push(sig);
          if (tag.Asset?.ParentSystemId != null) (this.systemIndex.get(tag.Asset.ParentSystemId) ?? this.systemIndex.set(tag.Asset.ParentSystemId, []).get(tag.Asset.ParentSystemId)!).push(sig);
          if (tag.AssetId != null) (this.assetIndex.get(tag.AssetId) ?? this.assetIndex.set(tag.AssetId, []).get(tag.AssetId)!).push(sig);
          if (tag.JBTStandardObservationId != null) (this.standardNameIndex.get(tag.JBTStandardObservationId) ?? this.standardNameIndex.set(tag.JBTStandardObservationId, []).get(tag.JBTStandardObservationId)!).push(sig);
          // ...your other one-time index hookups
        }
      } else {
        sig.set({...tag});             // write to the existing tag signal
        this.indexTagSignal(sig);               // ✅ NEW

      }

      this.setUpAlarmTags(tag);   // keep alarm indexes in sync
    }

    this.initializeIndexes = false;

    // Gate computeds that opt in (step 2)
    this.flushTick.update(v => v + 1);

    // If the component needs per-asset work, signal it now (emit, callback, etc.)
    // e.g., dispatch an event or invoke a provided callback with pendingAssetIds.

  }

  updateAssetsAffected(list: any) {
    const patchesByAsset = new Map<number, Partial<IAssetState>>();

    for (const { tag } of list) {
      const assetId = tag.AssetId;
      const stdId = tag.JBTStandardObservationId;
      if (assetId == null || stdId == null) continue;

      const mkPatch = this.STATE_FIELD_BY_STANDARD[stdId];
      if (!mkPatch) continue; // tag not used in CurrentState

      const patch = mkPatch(tag.LastObs?.Value);
      if (!patch) continue;

      const merged = patchesByAsset.get(assetId) ?? {};
      Object.assign(merged, patch);
      // optional: include TagNamePrefix once if you want it here
      patchesByAsset.set(assetId, merged);
      //console.log("patchesByAsset --> assetId: " + assetId + ", merged: %O", merged);
    }

    // 2c) hand the patches to your component
    this.assetStatePatches$.next(patchesByAsset);
  }

  setUpAlarmTags(updatedTag: ITag) {
    this.updateAlarmIndex(this.siteAlarmIndex, updatedTag.SiteId, updatedTag);
    this.updateAlarmIndex(this.systemAlarmIndex, updatedTag.Asset?.ParentSystemId, updatedTag);
    this.updateAlarmIndex(this.assetAlarmIndex, updatedTag.AssetId, updatedTag);
  }

  private updateAlarmIndex(indexMap: Map<number, WritableSignal<ITag>[]>, key: number | null | undefined, updatedTag: ITag) {
    if (key == null) return;

    const existingList = indexMap.get(key) ?? [];
    const idx = existingList.findIndex((tagSig) => tagSig().Id === updatedTag.Id);

    const shouldBeAlarm = (updatedTag.IsAlarm === true || updatedTag.IsCritical === true) && updatedTag.LastObs.Value === updatedTag.ValueWhenActive;

    if (shouldBeAlarm) {
      if (idx === -1) {
        const sig = signal(updatedTag);
        existingList.push(sig);
        indexMap.set(key, existingList);
      } else {
        // Optional: update the signal with latest data
        existingList[idx].set(updatedTag);
      }
    } else {
      if (idx !== -1) {
        existingList.splice(idx, 1);
        if (existingList.length > 0) {
          indexMap.set(key, existingList);
        } else {
          indexMap.delete(key);
        }
      }
    }
  }

  getAllTagsFromTagCache(): ITag[] {
    return Array.from(this.tagMap.values()).map((tagSignal) => tagSignal());
  }

  getAllTagsAsSignals(): Signal<ITag[]> {
    // Keep legacy API but ensure structural changes (initial cache load) are observable.
    return this.getAllTagsSignal();
  }

  getTagRecord(tagId: number): ITag | undefined {
    for (const sig of this.tagMap.values()) {
      const tag = sig();               // read the WritableSignal<ITag>
      if (tag?.Id === tagId) return tag;
    }
    return undefined;                  // not found
  }

  getTagsForSite(siteId: number): Signal<ITag[]> {
    console.log(this.serviceName + "getTagsForSite invoked. siteId: " + siteId + ", this.siteIndex = %O", this.siteIndex);
    if (!this.siteComputedMap.has(siteId)) {
      this.siteComputedMap.set(
        siteId,
        computed(() => this.siteIndex.get(siteId)?.map((sig) => sig()) ?? [])
      );
    }
    return this.siteComputedMap.get(siteId)!;
  }

  getAlarmTagsForSite(siteId: number): Signal<ITag[]> {
    if (!this.siteAlarmComputedMap.has(siteId)) {
      this.siteAlarmComputedMap.set(
        siteId,
        computed(
          () => {
            this.flushTick();
            const activeScope = Global.User?.currentUser?.SelectedAssetTypeGroupId;
            return this.siteAlarmIndex
              .get(siteId)
              ?.map((sig) => sig())
              .filter((tag) =>
                (tag.IsAlarm || tag.IsCritical) &&
                tag.LastObs?.Value === tag.ValueWhenActive &&
                tag.JBTStandardObservationId != 12349 &&
                tag.JBTStandardObservationId != 12324 &&
                this.isTagInAssetScope(tag, activeScope)
              ) ?? [];
          }
        )
      );
    }
    return this.siteAlarmComputedMap.get(siteId)!;
  }

  getTagsForSystem(systemId: number): Signal<ITag[]> {
    if (!this.systemComputedMap.has(systemId)) {
      this.systemComputedMap.set(
        systemId,
        computed(() => this.systemIndex.get(systemId)?.map((sig) => sig()) ?? [])
      );
    }
    return this.systemComputedMap.get(systemId)!;
  }

  getAlarmTagsForSystem(systemId: number): Signal<ITag[]> {
    if (!this.systemAlarmComputedMap.has(systemId)) {
      this.systemAlarmComputedMap.set(
        systemId,
        computed(
          () => {
            this.flushTick();
            const activeScope = Global.User?.currentUser?.SelectedAssetTypeGroupId;
            return this.systemAlarmIndex
              .get(systemId)
              ?.map((sig) => sig())
              .filter((tag) =>
                (tag.IsAlarm || tag.IsCritical) &&
                tag.LastObs?.Value === tag.ValueWhenActive &&
                tag.JBTStandardObservationId != 12349 &&
                tag.JBTStandardObservationId != 12324 &&
                this.isTagInAssetScope(tag, activeScope)
              ) ?? [];
          }
        )
      );
    }
    return this.systemAlarmComputedMap.get(systemId)!;
  }

  getTagsForAsset(assetId: number): Signal<ITag[]> {
    if (!this.assetComputedMap.has(assetId)) {
      this.assetComputedMap.set(
        assetId,
        computed(() => this.assetIndex.get(assetId)?.map((sig) => sig()) ?? [])
      );
    }
    return this.assetComputedMap.get(assetId)!;
  }

  getAlarmTagsForAsset(assetId: number): Signal<ITag[]> {
    if (!this.assetAlarmComputedMap.has(assetId)) {
      this.assetAlarmComputedMap.set(
        assetId,
        computed(
          () =>
            this.assetAlarmIndex
              .get(assetId)
              ?.map((sig) => sig())
              .filter((tag) => (tag.IsAlarm || tag.IsCritical) && tag.LastObs.Value === tag.ValueWhenActive && tag.JBTStandardObservationId != 12349 && tag.JBTStandardObservationId != 12324) ?? []
        )
      );
    }
    return this.assetAlarmComputedMap.get(assetId)!;
  }

  getTagsForStandardId(standardId: number): Signal<ITag[]> {
    if (!this.standardNameComputedMap.has(standardId)) {
      this.standardNameComputedMap.set(
        standardId,
        computed(() => this.standardNameIndex.get(standardId)?.map((sig) => sig()) ?? [])
      );
    }
    return this.standardNameComputedMap.get(standardId)!;
  }

  getTagsForTagIds(tagIds: number[]): Signal<ITag[]> {
    const key = tagIds.join(',');
    if (!this.tagIdComputedMap.has(key)) {
      this.tagIdComputedMap.set(
        key,
        computed(() => {
          const out: ITag[] = [];
          for (const id of tagIds) {
            if (!this.tagIdIndex.has(id)) this.ensureTagIdIndexed(id); // ✅ NEW
            const sig = this.tagIdIndex.get(id);
            if (!sig) continue;
            const tag = sig();
            if (tag) out.push(tag);
          }
          return out;
        })
      );
    }
    return this.tagIdComputedMap.get(key)!;
  }

  getActiveAlarmCountByTagIdAndSeverity(tagId: number, severity: string) {
    if (!this.tagIdAndSeverityComputedMap.has(tagId)) {
      this.tagIdAndSeverityComputedMap.set(
        tagId,
        computed(
          () =>
            this.tagIdAndSeverityIndex
              .get(tagId)
              ?.map((sig) => sig())
              .filter((tag) => tag.Severity === severity && (tag.IsAlarm || tag.IsCritical) && tag.LastObs.Value === tag.ValueWhenActive) ?? []
        )
      );
    }
    return this.tagIdAndSeverityComputedMap.get(tagId)!;
  }

  getMovePreventionTags(standardId: number): Signal<ITag[]> {
    if (!this.movePreventionComputedMap.has(standardId)) {
      this.movePreventionComputedMap.set(
        standardId,
        computed(() => this.movePreventionIndex.get(standardId)?.map((sig) => sig()) ?? [])
      );
    }
    return this.movePreventionComputedMap.get(standardId)!;
  }

  getElevatorRotundaTags(standardId: number): Signal<ITag[]> {
    if (!this.elevatorRotundaComputedMap.has(standardId)) {
      this.elevatorRotundaComputedMap.set(
        standardId,
        computed(() => this.elevatorRotundaIndex.get(standardId)?.map((sig) => sig()) ?? [])
      );
    }
    return this.elevatorRotundaComputedMap.get(standardId)!;
  }

  getPerfectTurnTags(category: "PBB" | "PCA" | "GPU" | "JetDock"): Signal<ITag[]> {
    if (!this.perfectTurnComputedMap.has(category)) {
      this.perfectTurnComputedMap.set(
        category,
        computed(() => this.perfectTurnIndex.get(category)?.map((sig) => sig()) ?? [])
      );
    }
    return this.perfectTurnComputedMap.get(category)!;
  }

  getOptionTags(standardId: number): Signal<ITag[]> {
    if (!this.optionTagComputedMap.has(standardId)) {
      this.optionTagComputedMap.set(
        standardId,
        computed(() => this.optionTagIndex.get(standardId)?.map((sig) => sig()) ?? [])
      );
    }
    return this.optionTagComputedMap.get(standardId)!;
  }

  getBucket() {
    return this.tagBucket.asReadonly();
  }

  getBucketConfig() {
    return this.tagBucketConfig.asReadonly();
  }

  setBucketConfig(config: ITagBucketConfig): void {
    this.tagBucketConfig.set({ ...config });
  }

  setBucketRange(startUtc?: number, endUtc?: number): void {
    this.tagBucketConfig.set({ startUtc, endUtc });
  }

  setBucketItems(items: IBucketItem[]): void {
    this.tagBucket.set([...items]);
  }

   addToBucket(tag: ITag, startUtc?: number, endUtc?: number): void {
    const exists = this.bucketIdSet().has(tag.Id);
    if (exists) {
      // If exists, update dates (idempotent)
      this.tagBucket.update(items =>
        items.map(i => i.tag.Id === tag.Id ? { ...i, tag, startUtc, endUtc } : i)
      );
    } else {
      this.tagBucket.update(items => [...items, { tag, startUtc, endUtc }]);
    }
  }

   setBucketDates(tagId: number, startUtc?: number, endUtc?: number): void {
    this.tagBucket.update(items =>
      items.map(i => i.tag.Id === tagId ? { ...i, startUtc, endUtc } : i)
    );
  }

  clearBucket(): void {
    this.tagBucket.set([]);
    this.tagBucketConfig.set({});
  }

  removeFromBucket(tagToRemove: ITag): void {
    this.removeFromBucketById(tagToRemove.Id);
  }

  // 1) Fast lookup of what's in the bucket (Set of IDs)

// 2) Is tag in bucket?
public isInBucket(tagId: number | null | undefined): boolean {
    if (tagId == null) return false;
    return this.bucketIdSet().has(tagId);
  }

// 3) Remove by id (keeps your existing removeByObject too)
removeFromBucketById(tagId: number): void {
    this.tagBucket.update(items => items.filter(i => i.tag.Id !== tagId));
  }


  /** DEV ONLY: directly set LastObs.Value by (assetId + stdId). Returns true if updated. */




debugSetLastObsValueByTagId(tagId: number, value: number): boolean {
  if (!isDevMode()) return false;
  // no need to resolve the tag first—just push; consumers watch the signal
  this.pushValue(tagId, value);
  return true;
}

  // Resolve a single tag by id as a Signal<ITag | null>
  getTagByIdSignal(tagId: number): Signal<ITag | null> {
    const multi = this.getTagsForTagIds([tagId]);
    return computed(() => {
      const arr = multi();
      return arr.length ? arr[0] : null;
    });
  }

  /** Try to register an id in tagIdIndex by scanning existing indexes once. */
  public ensureTagIdIndexed(tagId: number): boolean {
    if (this.tagIdIndex.has(tagId)) return true;

    // scan tagMap (fast path)
    const sig = this.tagMap.get(String(tagId));
    if (sig) { this.indexTagSignal(sig); return true; }

    // scan asset index (IDs are common here)
    for (const arr of this.assetIndex.values()) {
      for (const s of arr) {
        const t = s();
        if (t?.Id === tagId) { this.indexTagSignal(s); return true; }
      }
    }

    // scan standard/name indexes if needed
    for (const arr of this.standardNameIndex.values()) {
      for (const s of arr) {
        const t = s();
        if (t?.Id === tagId) { this.indexTagSignal(s); return true; }
      }
    }
    for (const arr of this.tagNameIndex.values()) {
      for (const s of arr) {
        const t = s();
        if (t?.Id === tagId) { this.indexTagSignal(s); return true; }
      }
    }

    return false;
  }

  public getAllTagsSignal(): Signal<ITag[]> {
  if (!this.allTagsComputed) {
    this.allTagsComputed = computed(() => {
      // Depend on flushTick so additions/removals trigger recompute
      this.flushTick();
      // Read each tag signal so value updates also trigger recompute
      const out: ITag[] = [];
      for (const sig of this.tagMap.values()) {
        const t = sig();
        if (t) out.push(t);
      }
      return out;
    });
  }
  return this.allTagsComputed;
}

public getAlarmTagsForOrganization(): Signal<ITag[]> {
  if (!this.allOrgAlarmTagsComputed) {
    this.allOrgAlarmTagsComputed = computed(() => {
      // Recompute when tags are added/removed or scope changes
      this.flushTick();

      const activeScope = Global.User?.currentUser?.SelectedAssetTypeGroupId;
      const out: ITag[] = [];
      for (const sig of this.tagMap.values()) {
        const tag = sig();                 // depend on each tag's signal
        if (!tag) continue;

        const isActiveAlarm =
          (tag.IsAlarm || tag.IsCritical) &&
          tag.LastObs?.Value === tag.ValueWhenActive &&
          tag.JBTStandardObservationId != 12349 &&  // ignore heartbeat/etc
          tag.JBTStandardObservationId != 12324;

        if (isActiveAlarm && this.isTagInAssetScope(tag, activeScope)) {
          out.push(tag);
        }
      }
      return out;
    });
  }
  return this.allOrgAlarmTagsComputed!;
}

public getAssetTypeGroupIdForTag(tag: ITag): number | undefined {
  const direct =
    tag?.Asset?.AssetType?.AssetTypeGroup?.Id ??
    tag?.Asset?.AssetType?.AssetTypeGroupId ??
    tag?.Asset?.AssetTypeGroupId;
  if (direct != null && Number.isFinite(Number(direct))) {
    return Number(direct);
  }

  const assetId = tag?.AssetId ?? tag?.Asset?.Id;
  if (assetId != null && this.assetsObject) {
    const cachedAsset = this.assetsObject[assetId];
    if (cachedAsset) {
      const cachedGroup =
        cachedAsset.AssetType?.AssetTypeGroup?.Id ??
        cachedAsset.AssetType?.AssetTypeGroupId ??
        cachedAsset.AssetTypeGroupId;
      if (cachedGroup != null && Number.isFinite(Number(cachedGroup))) {
        return Number(cachedGroup);
      }
    }
  }
  return undefined;
}

public isTagInAssetScope(tag: ITag, targetGroupId?: number | null): boolean {
  const activeScope = targetGroupId !== undefined ? targetGroupId : (Global.User?.currentUser?.SelectedAssetTypeGroupId ?? null);
  if (activeScope !== 1 && activeScope !== 2) {
    return true; // No scope restriction
  }
  const tagGroup = this.getAssetTypeGroupIdForTag(tag);
  return tagGroup === activeScope;
}

public refreshForScopeChange(): void {
  this.flushTick.update(v => v + 1);
}

}
